// src/pages/ProfilePage.jsx
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const [pwdData, setPwdData] = useState({
    current_password: '', new_password: '', confirm_password: '',
  });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMessage, setPwdMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('infos');

  useEffect(() => {
    const token  = localStorage.getItem('auth_token');
    const stored = localStorage.getItem('user_data');

    if (!token) { navigate('/login'); return; }

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed);
        setFormData({
          name:    parsed.name    || '',
          email:   parsed.email   || '',
          phone:   parsed.phone   || '',
          address: parsed.address || '',
        });
      } catch { /* ignore */ }
    }

    axios
      .get('https://madadline.com/api/user', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        const u = res.data;
        setUser(u);
        setFormData({
          name:    u.name    || '',
          email:   u.email   || '',
          phone:   u.phone   || '',
          address: u.address || '',
        });
        localStorage.setItem('user_data', JSON.stringify(u));
      })
      .catch(err => {
        console.error('Erreur profil:', err);
        setError(t('profile.loadingError'));
      })
      .finally(() => setLoading(false));
  }, [navigate, t]);

  const handleSaveInfos = async (e) => {
    e.preventDefault();
    setSaveMessage(null);
    setSaving(true);

    try {
      const token = localStorage.getItem('auth_token');
      const userId = localStorage.getItem('user_id');

      const { data } = await axios.put(
        `https://madadline.com/api/users-edit/${userId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedUser = data.user || data;
      setUser(updatedUser);
      localStorage.setItem('user_data', JSON.stringify(updatedUser));

      setSaveMessage({ type: 'success', text: t('profile.successInfos') });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      console.error('Erreur update profil:', err);
      setSaveMessage({
        type: 'error',
        text: err.response?.data?.message || t('profile.errorInfos'),
      });
      setTimeout(() => setSaveMessage(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdMessage(null);

    if (pwdData.new_password.length < 8) {
      setPwdMessage({ type: 'error', text: t('profile.errorPasswordShort') });
      return;
    }
    if (pwdData.new_password !== pwdData.confirm_password) {
      setPwdMessage({ type: 'error', text: t('profile.errorPasswordMismatch') });
      return;
    }

    setPwdSaving(true);
    try {
      const token  = localStorage.getItem('auth_token');
      const userId = localStorage.getItem('user_id');

      await axios.put(
        `https://madadline.com/api/users-edit/${userId}`,
        {
          password:              pwdData.new_password,
          password_confirmation: pwdData.confirm_password,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPwdData({ current_password: '', new_password: '', confirm_password: '' });
      setPwdMessage({ type: 'success', text: t('profile.successPassword') });
      setTimeout(() => setPwdMessage(null), 3000);
    } catch (err) {
      console.error('Erreur mot de passe:', err);
      setPwdMessage({
        type: 'error',
        text: err.response?.data?.message || t('profile.errorPassword'),
      });
      setTimeout(() => setPwdMessage(null), 4000);
    } finally {
      setPwdSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_data');
    localStorage.removeItem('cart');
    navigate('/');
  };

  const getInitials = () => {
    const name = user?.name || user?.email || 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name[0].toUpperCase();
  };

  if (loading) {
    return (
      <main className="profile-page">
        <div className="profile-page__loading"><div className="spinner" /></div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="profile-page">
        <div className="profile-page__empty">
          <h2>{t('profile.errorTitle')}</h2>
          <p>{error}</p>
          <Link to="/" className="btn btn-primary">{t('profile.backHome')}</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="profile-page">

      <header className="profile-page__head">
        <div className="profile-page__head-inner">
          <span className="profile-page__label">{t('profile.heroLabel')}</span>
          <h1 className="profile-page__title">{t('profile.heroTitle')}</h1>
          <p className="profile-page__subtitle">{t('profile.heroSubtitle')}</p>
        </div>
      </header>

      <section className="profile-page__content">

        <aside className="profile-card">
          <div className="profile-card__avatar">
            {user?.avatar ? (
              <img
                src={user.avatar.startsWith('http') ? user.avatar : `https://madadline.com/${user.avatar}`}
                alt={user.name}
              />
            ) : (
              <span>{getInitials()}</span>
            )}
          </div>

          <h2 className="profile-card__name">{user?.name || t('profile.userFallback')}</h2>
          <p className="profile-card__email">{user?.email || ''}</p>

          <span className="profile-card__badge">{t('profile.verifiedBadge')}</span>

          <nav className="profile-card__nav">
            <button
              className={`profile-card__nav-item ${activeTab === 'infos' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('infos')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
              </svg>
              {t('profile.tabInfos')}
            </button>
            <button
              className={`profile-card__nav-item ${activeTab === 'password' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('password')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              {t('profile.tabPassword')}
            </button>
            <Link to="/cart" className="profile-card__nav-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              {t('profile.tabOrders')}
            </Link>
            <Link to="/notifications" className="profile-card__nav-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {t('profile.tabNotifications')}
            </Link>
          </nav>

          <button onClick={handleLogout} className="profile-card__logout">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {t('profile.logout')}
          </button>
        </aside>

        <div className="profile-content">

          {activeTab === 'infos' && (
            <div className="profile-panel">
              <header className="profile-panel__head">
                <h2>{t('profile.infosHeading')}</h2>
                <p>{t('profile.infosSubheading')}</p>
              </header>

              {saveMessage && (
                <div className={`profile-alert profile-alert--${saveMessage.type}`}>
                  {saveMessage.text}
                </div>
              )}

              <form className="profile-form" onSubmit={handleSaveInfos}>
                <div className="profile-form__group">
                  <label htmlFor="name">{t('profile.labelName')}</label>
                  <input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('profile.placeholderName')}
                    required
                  />
                </div>

                <div className="profile-form__group">
                  <label htmlFor="email">{t('profile.labelEmail')}</label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder={t('profile.placeholderEmail')}
                    required
                  />
                </div>

                <div className="profile-form__group">
                  <label htmlFor="phone">{t('profile.labelPhone')}</label>
                  <input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder={t('profile.placeholderPhone')}
                  />
                </div>

                <div className="profile-form__group">
                  <label htmlFor="address">{t('profile.labelAddress')}</label>
                  <textarea
                    id="address"
                    rows="3"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder={t('profile.placeholderAddress')}
                  />
                </div>

                <button type="submit" className="profile-form__submit" disabled={saving}>
                  {saving ? t('profile.submittingInfos') : t('profile.submitInfos')}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'password' && (
            <div className="profile-panel">
              <header className="profile-panel__head">
                <h2>{t('profile.passwordHeading')}</h2>
                <p>{t('profile.passwordSubheading')}</p>
              </header>

              {pwdMessage && (
                <div className={`profile-alert profile-alert--${pwdMessage.type}`}>
                  {pwdMessage.text}
                </div>
              )}

              <form className="profile-form" onSubmit={handleChangePassword}>
                <div className="profile-form__group">
                  <label htmlFor="current">{t('profile.labelCurrentPassword')}</label>
                  <input
                    id="current"
                    type="password"
                    value={pwdData.current_password}
                    onChange={(e) => setPwdData({ ...pwdData, current_password: e.target.value })}
                    placeholder={t('profile.placeholderCurrentPassword')}
                    required
                  />
                </div>

                <div className="profile-form__group">
                  <label htmlFor="new">{t('profile.labelNewPassword')}</label>
                  <input
                    id="new"
                    type="password"
                    value={pwdData.new_password}
                    onChange={(e) => setPwdData({ ...pwdData, new_password: e.target.value })}
                    placeholder={t('profile.placeholderNewPassword')}
                    required
                  />
                </div>

                <div className="profile-form__group">
                  <label htmlFor="confirm">{t('profile.labelConfirmPassword')}</label>
                  <input
                    id="confirm"
                    type="password"
                    value={pwdData.confirm_password}
                    onChange={(e) => setPwdData({ ...pwdData, confirm_password: e.target.value })}
                    placeholder={t('profile.placeholderConfirmPassword')}
                    required
                  />
                </div>

                <button type="submit" className="profile-form__submit" disabled={pwdSaving}>
                  {pwdSaving ? t('profile.submittingPassword') : t('profile.submitPassword')}
                </button>
              </form>
            </div>
          )}

        </div>
      </section>
    </main>
  );
}

export default ProfilePage;